import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useUrlState } from '@/lib/useUrlState';
import { useParentShell } from './ParentShell';

/**
 * Дополнительные занятия в ЛК родителя — две вкладки:
 *
 *   • Проведённые — календарь по месяцу. На каждый день показываем
 *     столбиком проведённые занятия (предмет + время + педагог).
 *     Клик на день/занятие пока в попап не разворачиваем — всё видно
 *     в самой ячейке (занятий обычно немного).
 *
 *   • Баланс — приход / расход / остаток по `paid_services`-договору
 *     выбранного года. Логика 1-в-1 с админским отчётом
 *     «Балансы по доп. услугам» (тот же SQL за кулисами).
 *
 * Источник данных — `GET /api/parent/extras?student_id=&year_id=&month=`.
 * Бэк сам резолвит права (родитель должен быть в `parent_students` своего
 * ребёнка) и возвращает оба раздела одним запросом.
 */
const URL_SCHEMA = { tab: 'days', month: '' };

const MONTHS_RU = [
  'Январь','Февраль','Март','Апрель','Май','Июнь',
  'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь',
];
const DOW_LABELS = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

function fmtMoney(v) {
  const n = Number(v) || 0;
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ₽';
}
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}
function monthAdd(ym, n) {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
}
function dayOfWeek(iso) {
  // 1 (Пн) .. 7 (Вс)
  const d = new Date(iso + 'T00:00:00');
  return d.getDay() || 7;
}

export default function ParentExtrasPage() {
  const { child, activeYear } = useParentShell();
  const [params, setParams] = useUrlState(URL_SCHEMA);
  const tab = params.tab === 'balance' ? 'balance' : 'days';

  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!child || !activeYear) return;
    let cancelled = false;
    setLoad(true); setError(null);
    const qs = new URLSearchParams({ student_id: String(child.id), year_id: String(activeYear.id) });
    if (params.month) qs.set('month', params.month);
    api.get('/api/parent/extras?' + qs.toString())
      .then((res) => {
        if (cancelled) return;
        setData(res);
        // Если в URL не было месяца — синхронизируем (бэк выбрал текущий).
        if (!params.month && res.month) setParams({ month: res.month });
      })
      .catch((e) => { if (!cancelled) setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'); })
      .finally(() => { if (!cancelled) setLoad(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [child?.id, activeYear?.id, params.month]);

  if (loading) return <div className="pc-body" style={{ color: 'var(--pc-fg-faint)' }}>Загружаем доп. занятия…</div>;
  if (error)   return <div className="pc-body" style={{ color: 'var(--pc-warn)' }}>Ошибка: {error}</div>;
  if (!data)   return null;

  const { agreement, balance, monthSummary } = data;
  const month = data.month || params.month;

  if (!agreement) {
    return (
      <div className="pc-body">
        <div className="pc-card">
          <div className="pc-card__head">
            <div className="pc-card__title">Дополнительные занятия</div>
            <span className="pc-card__sub">{data.yearStudy?.name}</span>
          </div>
          <div className="pc-card__body" style={{ color: 'var(--pc-fg-faint)' }}>
            На этот учебный год нет договора «Доп. услуги». Если он должен быть — обратитесь в школу.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pc-body">
      <div className="pc-card">
        <div className="pc-card__head">
          <div className="pc-card__title">Дополнительные занятия</div>
          <span className="pc-card__sub">
            {data.yearStudy?.name}
            {agreement.number && <> · договор № {agreement.number}</>}
          </span>
        </div>
        <div className="pc-card__body" style={{ paddingTop: 0 }}>
          {/* Вкладки */}
          <div className="pc-tabs">
            <button
              type="button"
              className={'pc-tab' + (tab === 'days'    ? ' is-active' : '')}
              onClick={() => setParams({ tab: 'days' })}>
              Проведённые
            </button>
            <button
              type="button"
              className={'pc-tab' + (tab === 'balance' ? ' is-active' : '')}
              onClick={() => setParams({ tab: 'balance' })}>
              Баланс
            </button>
          </div>

          {tab === 'days' ? (
            <DaysCalendar
              month={month}
              days={data.days || []}
              daysInMonth={data.daysInMonth || 30}
              summary={monthSummary}
              setMonth={(m) => setParams({ month: m })}
            />
          ) : (
            <BalancePanel balance={balance} agreement={agreement} />
          )}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────── Календарь проведённых ─────────────────────── */

function DaysCalendar({ month, days, daysInMonth, summary, setMonth }) {
  // Раскладка по неделям: первая колонка — Пн. Перед 1-м числом — пустые
  // ячейки до нужного дня недели. Это даёт привычную «месячную сетку».
  const cells = useMemo(() => {
    if (!days || !days.length) return [];
    const firstIso = days[0].date;
    const firstDow = dayOfWeek(firstIso);
    const blanks = Array.from({ length: firstDow - 1 }, () => null);
    return [...blanks, ...days];
  }, [days]);

  if (!month) return null;

  const [y, m] = month.split('-').map(Number);
  const title = `${MONTHS_RU[(m || 1) - 1]} ${y}`;

  return (
    <div className="pc-extras-cal">
      {/* Шапка с навигацией по месяцам и итогом */}
      <div className="pc-extras-cal__head">
        <div className="pc-extras-cal__nav">
          <button type="button" className="pc-extras-cal__btn"
                  onClick={() => setMonth(monthAdd(month, -1))} title="Предыдущий месяц">
            <Icon name="chevron-left" size={14} />
          </button>
          <span className="pc-extras-cal__title">{title}</span>
          <button type="button" className="pc-extras-cal__btn"
                  onClick={() => setMonth(monthAdd(month, 1))} title="Следующий месяц">
            <Icon name="chevron" size={14} />
          </button>
        </div>
        {summary && (summary.count > 0) && (
          <div className="pc-extras-cal__summary">
            За месяц: <b>{summary.count}</b> зан. ·{' '}
            <b className="pc-extras-cal__sum">{fmtMoney(summary.sum)}</b>
          </div>
        )}
      </div>

      {/* Дни недели — показываем только в desktop-раскладке */}
      <div className="pc-extras-cal__dow">
        {DOW_LABELS.map((d) => <div key={d} className="pc-extras-cal__dow-cell">{d}</div>)}
      </div>

      {/* Сетка дней — десктоп. На мобилке скрывается через CSS. */}
      <div className="pc-extras-cal__grid">
        {cells.map((cell, i) => {
          if (!cell) return <div key={'b' + i} className="pc-extras-cal__cell pc-extras-cal__cell--blank" />;
          const isWeekend = dayOfWeek(cell.date) >= 6;
          const has = (cell.lessons || []).length > 0;
          return (
            <div key={cell.date}
                 className={'pc-extras-cal__cell'
                   + (isWeekend ? ' is-weekend' : '')
                   + (has ? ' has-lessons' : '')}>
              <div className="pc-extras-cal__day">{cell.day}</div>
              {(cell.lessons || []).map((l) => (
                <div key={l.id} className="pc-extras-cal__lesson"
                     title={`${l.subjectName}${l.teacherName ? ' · ' + l.teacherName : ''}${l.roomName ? ' · каб. ' + l.roomName : ''}`}>
                  <div className="pc-extras-cal__lesson-subj">{l.subjectName}</div>
                  <div className="pc-extras-cal__lesson-meta">
                    {l.timeStart && <span className="mono">{l.timeStart}</span>}
                    {l.teacherName && <span> · {l.teacherName}</span>}
                  </div>
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Лента по дням — мобилка. На десктопе скрывается через CSS.
          Показываем только дни с занятиями — пустые дни в столбике
          ничего не сообщают и только занимают экран. */}
      <div className="pc-extras-cal__agenda">
        {(days || []).filter((d) => (d.lessons || []).length > 0).map((d) => (
          <div key={d.date} className="pc-extras-cal__ag-day">
            <div className="pc-extras-cal__ag-date">
              <span className="pc-extras-cal__ag-num">{d.day}</span>
              <span className="pc-extras-cal__ag-dow">{DOW_FULL_RU[(dayOfWeek(d.date) - 1)]}</span>
            </div>
            <div className="pc-extras-cal__ag-lessons">
              {(d.lessons || []).map((l) => (
                <div key={l.id} className="pc-extras-cal__ag-lesson">
                  <div className="pc-extras-cal__ag-time mono">
                    {l.timeStart}{l.timeEnd ? '–' + l.timeEnd : ''}
                  </div>
                  <div className="pc-extras-cal__ag-body">
                    <div className="pc-extras-cal__ag-subj">{l.subjectName}</div>
                    <div className="pc-extras-cal__ag-meta">
                      {l.teacherName}
                      {l.roomName ? ' · каб. ' + l.roomName : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {(!days || !days.some((d) => (d.lessons || []).length > 0)) && (
        <div style={{ color: 'var(--pc-fg-faint)', fontSize: 13, padding: '12px 4px' }}>
          В этом месяце проведённых доп. занятий ещё нет.
        </div>
      )}
    </div>
  );
}

const DOW_FULL_RU = ['понедельник','вторник','среда','четверг','пятница','суббота','воскресенье'];

/* ──────────────────────── Остаток по предметам ──────────────────────── */

function SubjectsBalance({ subjects }) {
  if (!subjects || subjects.length === 0) return null;
  // Числа округляем по правилам отчёта: rub без копеек (так в админке),
  // занятий — до целого вниз (заявок «у меня 0.5 занятия» не бывает).
  const fmtRub = (v) => {
    const n = Math.round(Number(v) || 0);
    return n.toLocaleString('ru-RU');
  };
  return (
    <div className="pc-extras-subjects">
      <div className="pc-extras-subjects__title">Остаток по предметам</div>
      <div className="pc-extras-subjects__list">
        {subjects.map((s, i) => {
          const negative = s.remainingAmount < 0;
          const sessions = Math.trunc(s.remainingCount || 0);
          return (
            <div key={i} className={'pc-extras-subj' + (negative ? ' is-debt' : '')}>
              <div className="pc-extras-subj__name">{s.label}</div>
              <div className="pc-extras-subj__nums">
                <div className={'pc-extras-subj__rub ' + (negative ? 'is-debt' : 'is-paid')}>
                  {negative ? '' : '+'}{fmtRub(s.remainingAmount)} ₽
                </div>
                <div className="pc-extras-subj__cnt">
                  {sessions > 0
                    ? <>{sessions} {pluralRu(sessions, ['занятие','занятия','занятий'])}</>
                    : sessions < 0
                      ? <>−{Math.abs(sessions)} {pluralRu(Math.abs(sessions), ['занятие','занятия','занятий'])} ({'в минусе'})</>
                      : <span className="muted">0 занятий</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Простой склонятор «1 занятие / 2 занятия / 5 занятий». */
function pluralRu(n, forms) {
  const x = Math.abs(n) % 100;
  if (x >= 11 && x <= 14) return forms[2];
  const y = x % 10;
  if (y === 1) return forms[0];
  if (y >= 2 && y <= 4) return forms[1];
  return forms[2];
}

/* ──────────────────────── Баланс ──────────────────────── */

function BalancePanel({ balance, agreement }) {
  const negative = balance && balance.balance < 0;
  const hasAvg   = !!(balance && balance.avgPrice && balance.avgPrice > 0);
  return (
    <div className="pc-extras-balance">
      <div className="pc-extras-balance__cards">
        <div className="pc-extras-balance__card">
          <div className="pc-extras-balance__cap">Приход</div>
          <div className="pc-extras-balance__num pc-extras-balance__num--paid">
            {fmtMoney(balance?.income || 0)}
          </div>
          <div className="pc-extras-balance__hint">оплачено по договору</div>
        </div>
        <div className="pc-extras-balance__card">
          <div className="pc-extras-balance__cap">Расход</div>
          <div className="pc-extras-balance__num">
            {fmtMoney(balance?.expense || 0)}
          </div>
          <div className="pc-extras-balance__hint">
            проведённые занятия
            {balance?.attendedCount > 0 && <> · {balance.attendedCount} зан.</>}
          </div>
        </div>
        <div className={'pc-extras-balance__card' + (negative ? ' is-debt' : '')}>
          <div className="pc-extras-balance__cap">Остаток</div>
          <div className={'pc-extras-balance__num ' + (negative ? 'pc-extras-balance__num--debt' : 'pc-extras-balance__num--paid')}>
            {fmtMoney(balance?.balance || 0)}
          </div>
          <div className="pc-extras-balance__hint">
            {negative ? 'требуется доплата' : 'доступно на занятия'}
          </div>
        </div>
      </div>

      {/* Остаток по предметам. Тот же бакетинг, что в админском
          отчёте балансов (одинаковые числа), но колонок две: предмет
          и остаток (рубли + занятий). Строки, где остаток в рублях
          округляется к нулю — прячем (на UI «+0 ₽» сбивает с толку,
          даже если в счётчике занятий 9 — это в основном «бесплатные
          пробники», не настоящий остаток). */}
      <SubjectsBalance subjects={(balance?.subjects || []).filter((s) => Math.round(s.remainingAmount || 0) !== 0)} />

      <div className="pc-extras-balance__info">
        <div>
          <span className="muted">Договор:</span> № {agreement.number || '—'} от {fmtDateRu(agreement.date)}
        </div>
        <div>
          <span className="muted">Период:</span> {fmtDateRu(agreement.dateStart)} — {fmtDateRu(agreement.dateEnd)}
          {agreement.dateTermination && (
            <span style={{ color: 'var(--pc-warn)' }}> · расторгнут {fmtDateRu(agreement.dateTermination)}</span>
          )}
        </div>
        {negative && agreement.payUrl && (
          <a href={agreement.payUrl} target="_blank" rel="noopener noreferrer"
             className="btn btn--primary btn--sm" style={{ marginTop: 10, display: 'inline-flex' }}>
            <Icon name="wallet" size={13} /> Пополнить баланс
          </a>
        )}
      </div>
    </div>
  );
}
